import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ClipboardCheck, Award, Clock, ChevronRight, ShieldAlert, Key, CheckSquare } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import api from '../../services/api'

const RED    = '#FF3B5C'
const TEAL   = '#00D4AA'
const ORANGE = '#FF9500'
const PURPLE = '#BF5AF2'
const BLUE   = '#0A84FF'

export default function MobileHome() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [stats, setStats] = useState({ incidents: 0, audits: 0, expiring: 0, overdue: 0 })
  const [expiring, setExpiring] = useState<any[]>([])
  const [activeSession, setActiveSession] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      api.get('/incidents?status=open&limit=1').catch(() => ({ data: { total: 0 } })),
      api.get('/audits?status=scheduled&limit=1').catch(() => ({ data: { total: 0 } })),
      api.get('/competencies/expiring?days=90').catch(() => ({ data: { data: [] } })),
      api.get('/corrective-actions?status=overdue&limit=1').catch(() => ({ data: { total: 0 } })),
      api.get('/lone-worker/active').catch(() => ({ data: null })),
    ]).then(([inc, aud, exp, ov, lw]) => {
      setStats({
        incidents: inc.data.total || 0,
        audits: aud.data.total || 0,
        expiring: exp.data.data?.length || 0,
        overdue: ov.data.total || 0,
      })
      setExpiring((exp.data.data || []).slice(0, 4))
      setActiveSession(!!lw.data)
      setLoading(false)
    })
  }, [])

  const greeting = () => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    return 'Good evening'
  }

  const today = new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })

  const METRICS = [
    { label: 'OPEN INCIDENTS',   value: stats.incidents, color: RED,    icon: AlertTriangle,  nav: '/m/incidents' },
    { label: 'SCHEDULED AUDITS', value: stats.audits,    color: TEAL,   icon: ClipboardCheck, nav: '/m/audits' },
    { label: 'EXPIRING LICENCES',value: stats.expiring,  color: ORANGE, icon: Award,          nav: '/m/licences' },
    { label: 'OVERDUE ACTIONS',  value: stats.overdue,   color: PURPLE, icon: Clock,          nav: '/m/corrective-actions' },
  ]

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%', paddingBottom: '1.5rem' }}>

      {/* Greeting */}
      <div style={{ padding: '1.25rem 1.25rem 1rem' }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>
          {today}
        </p>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: '#FFFFFF', lineHeight: 1.15, letterSpacing: '-0.02em', margin: 0 }}>
          {greeting()},<br />{user?.firstName}
        </h1>
      </div>

      {/* Metrics grid */}
      <div style={{ padding: '0 1.25rem 1.25rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {METRICS.map(({ label, value, color, icon: Icon, nav }) => (
            <button
              key={label}
              onClick={() => nav && navigate(nav)}
              style={{
                background: 'var(--white)',
                border: 'none',
                borderRadius: 20,
                padding: '1rem',
                textAlign: 'left',
                cursor: nav ? 'pointer' : 'default',
                position: 'relative',
                overflow: 'hidden',
              }}
              className="active:scale-95 transition-transform"
            >
              {/* Subtle colored glow in corner */}
              <div style={{
                position: 'absolute', top: 0, right: 0,
                width: 60, height: 60,
                borderRadius: '0 20px 0 60px',
                background: `${color}18`,
                pointerEvents: 'none',
              }} />
              <div style={{ marginBottom: 10 }}>
                <Icon size={16} color={color} strokeWidth={2.2} />
              </div>
              <div style={{ fontSize: 40, fontWeight: 900, color, lineHeight: 1, letterSpacing: '-0.03em' }}>
                {loading ? '—' : value}
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', marginTop: 6, textTransform: 'uppercase' }}>
                {label}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Panic / SOS — always above the fold */}
      <div style={{ padding: '0 1.25rem 1.25rem' }}>
        <button
          onClick={() => navigate(activeSession ? '/m/lone-worker/active' : '/m/lone-worker')}
          style={{
            width: '100%',
            background: activeSession
              ? 'linear-gradient(135deg, #7F1D1D, #991B1B)'
              : 'linear-gradient(135deg, #2D0A0A, #4C1010)',
            border: `2px solid ${activeSession ? '#EF4444' : '#EF4444'}`,
            borderRadius: 20,
            padding: '1rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
          className="active:scale-95 transition-transform"
        >
          {activeSession && (
            <div style={{
              position: 'absolute', inset: 0, borderRadius: 18,
              border: '2px solid rgba(239,68,68,0.5)',
              animation: 'pulse 2s ease-in-out infinite',
            }} />
          )}
          <div style={{
            width: 48, height: 48, borderRadius: 14, flexShrink: 0,
            background: '#EF4444',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 16px rgba(239,68,68,0.6)',
          }}>
            <ShieldAlert size={24} color="white" strokeWidth={2.5} />
          </div>
          <div style={{ textAlign: 'left', flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 900, color: '#FFFFFF', letterSpacing: '-0.01em', textTransform: 'uppercase' }}>
              {activeSession ? '⚠ SESSION ACTIVE' : 'PANIC / SOS'}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
              {activeSession ? 'Tap to open SOS screen' : 'Lone worker safety & emergency'}
            </div>
          </div>
          <ChevronRight size={20} color="rgba(255,255,255,0.5)" />
        </button>
      </div>

      {/* Quick actions */}
      <div style={{ padding: '0 1.25rem 1.25rem' }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
          Quick Actions
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button
            onClick={() => navigate('/m/incidents/new')}
            style={{
              background: `linear-gradient(145deg, ${RED}30, ${RED}10)`,
              border: `1px solid ${RED}35`,
              borderRadius: 20,
              padding: '1rem',
              textAlign: 'left',
              minHeight: 110,
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
            }}
            className="active:scale-95 transition-transform"
          >
            <div style={{ width: 38, height: 38, borderRadius: 12, background: RED, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <AlertTriangle size={18} color="white" />
            </div>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#FFFFFF', lineHeight: 1.3 }}>Report Incident</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>Log an unsafe event</span>
          </button>

          <button
            onClick={() => navigate('/m/audits')}
            style={{
              background: `linear-gradient(145deg, ${TEAL}30, ${TEAL}10)`,
              border: `1px solid ${TEAL}35`,
              borderRadius: 20,
              padding: '1rem',
              textAlign: 'left',
              minHeight: 110,
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
            }}
            className="active:scale-95 transition-transform"
          >
            <div style={{ width: 38, height: 38, borderRadius: 12, background: TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <ClipboardCheck size={18} color="white" />
            </div>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#FFFFFF', lineHeight: 1.3 }}>Start Audit</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>Conduct a site audit</span>
          </button>

          <button
            onClick={() => navigate('/m/permits')}
            style={{
              background: `linear-gradient(145deg, ${BLUE}30, ${BLUE}10)`,
              border: `1px solid ${BLUE}35`,
              borderRadius: 20,
              padding: '1rem',
              textAlign: 'left',
              minHeight: 110,
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
            }}
            className="active:scale-95 transition-transform"
          >
            <div style={{ width: 38, height: 38, borderRadius: 12, background: BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <Key size={18} color="white" />
            </div>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#FFFFFF', lineHeight: 1.3 }}>View Permits</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>Active permits to work</span>
          </button>

          <button
            onClick={() => navigate('/m/corrective-actions')}
            style={{
              background: `linear-gradient(145deg, ${PURPLE}30, ${PURPLE}10)`,
              border: `1px solid ${PURPLE}35`,
              borderRadius: 20,
              padding: '1rem',
              textAlign: 'left',
              minHeight: 110,
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
            }}
            className="active:scale-95 transition-transform"
          >
            <div style={{ width: 38, height: 38, borderRadius: 12, background: PURPLE, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <CheckSquare size={18} color="white" />
            </div>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#FFFFFF', lineHeight: 1.3 }}>My Actions</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>
              {stats.overdue > 0 ? `${stats.overdue} overdue` : 'Corrective actions'}
            </span>
          </button>
        </div>
      </div>

      {/* Expiring soon */}
      {expiring.length > 0 && (
        <div style={{ padding: '0 1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.12em', textTransform: 'uppercase', margin: 0 }}>
              Expiring Soon
            </p>
            <button
              onClick={() => navigate('/m/licences')}
              style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, fontWeight: 600, color: ORANGE, cursor: 'pointer' }}
            >
              See all <ChevronRight size={13} />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {expiring.map(lic => {
              const days = Math.ceil((new Date(lic.expiry_date).getTime() - Date.now()) / 86400000)
              const color = days <= 30 ? RED : ORANGE
              return (
                <div
                  key={lic.id}
                  style={{
                    background: 'var(--white)',
                    borderRadius: 16,
                    borderLeft: `3px solid ${color}`,
                    padding: '12px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lic.worker_name}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lic.competency_name}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color }}>{days}d</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>left</div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
